/**
 * VirulentTouchSkillEffect — Poison tree evolution: triple-contact primer.
 *
 * Replaces Taint. On activation, arms the player's next 3 contact hits.
 * Each hit independently applies a full `poison` DoT — same intensity and
 * duration as upgraded Taint (Potent Compound + Lingering Formula via
 * resolver queries on 'taint'). The three applications use independent
 * stacking so a single target can carry 3 simultaneous poison timers.
 *
 * Independence is achieved via unique sourceSkillId per application:
 *   'virulent_touch_1', 'virulent_touch_2', 'virulent_touch_3'
 * combined with `stackingRuleOverride: 'independent'`, bypassing the
 * global `refresh` rule for Virulent Touch's applications only.
 *
 * Supports the same upgrade keys as Taint (enhancesId: 'taint'):
 * - Potent Compound: `dotDamagePerTick` (additive) — +1 per tick
 * - Lingering Formula: `dotDurationMs` (additive) — +2000ms
 * - Toxicologist (tree passive): `dotDamageMultiplier` (product) — ×1.25
 *
 * Cooldown tier: medium (1200ms).
 *
 * Pure logic — no Phaser deps.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-066 (source-tagged DoT coexistence)
 * @see poison-spec.md § Virulent Touch
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface VirulentTouchDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getTreePassiveEffect(
      treeName: string,
      key: string,
      aggregation: 'sum' | 'product' | 'max' | 'min',
    ): number;
  };
  readonly statusEffects: {
    apply(defId: string, opts: {
      sourceSkillId: string;
      durationMs: number;
      intensity: number;
      stackingRuleOverride?: string;
    }): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface VirulentTouchConfig {
  readonly cooldownMs: number;       // medium tier = 1200ms
  readonly primeWindowMs: number;    // window before prime counter lapses
  readonly hitCharges: number;       // number of hits armed per activation (3)
  readonly baseIntensity: number;    // base DoT damage per tick (from Taint)
  readonly baseDurationMs: number;   // base DoT duration (from Taint)
}

const DEFAULT_CONFIG: VirulentTouchConfig = {
  cooldownMs: 1200,
  primeWindowMs: 8000,
  hitCharges: 3,
  baseIntensity: 3,
  baseDurationMs: 4000,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class VirulentTouchSkillEffect implements SkillEffect {
  readonly skillId = 'virulent_touch';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: VirulentTouchDeps;
  private readonly config: VirulentTouchConfig;

  private _cooldownRemaining = 0;
  private _primeCounter = 0;
  private _primeWindowRemaining = 0;
  private _activationsCount = 0;
  private _hitsPoisoned = 0;
  private _primeTimeouts = 0;
  /** Running application index for unique sourceSkillId generation. */
  private _applicationIndex = 0;

  constructor(deps: VirulentTouchDeps, config: Partial<VirulentTouchConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get primeCounter(): number { return this._primeCounter; }
  get isPrimed(): boolean { return this._primeCounter > 0; }
  get primeWindowRemaining(): number { return this._primeWindowRemaining; }
  get activationsCount(): number { return this._activationsCount; }
  get hitsPoisoned(): number { return this._hitsPoisoned; }
  get primeTimeouts(): number { return this._primeTimeouts; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this._primeCounter = this.config.hitCharges;
    this._primeWindowRemaining = this.config.primeWindowMs;
    this._cooldownRemaining = this.config.cooldownMs;
    this._activationsCount++;

    return true;
  }

  deactivate(): void {
    // Instant — no toggle
  }

  isActive(): boolean {
    return false;
  }

  /**
   * Called by CombatHitResolver when any contact hit (melee or Blades) lands.
   * If primed, applies one independent poison DoT to the target and decrements
   * the prime counter. Returns true if poison was applied, false if not primed.
   */
  notifyHitLanded(): boolean {
    if (this._primeCounter <= 0) return false;

    this._applicationIndex++;
    const sourceSkillId = `virulent_touch_${this._applicationIndex}`;

    const intensity = this.resolveIntensity();
    const durationMs = this.resolveDurationMs();

    this.deps.statusEffects.apply('poison', {
      sourceSkillId,
      durationMs,
      intensity,
      stackingRuleOverride: 'independent',
    });

    this._primeCounter--;
    this._hitsPoisoned++;

    if (this._primeCounter === 0) {
      this._primeWindowRemaining = 0;
    }

    return true;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    if (this._primeCounter > 0) {
      this._primeWindowRemaining -= delta;
      if (this._primeWindowRemaining <= 0) {
        // Prime lapses silently — no cooldown re-trigger
        this._primeCounter = 0;
        this._primeWindowRemaining = 0;
        this._primeTimeouts++;
      }
    }
  }

  destroy(): void {
    this._primeCounter = 0;
    this._primeWindowRemaining = 0;
    this._cooldownRemaining = 0;
  }

  // ---------------------------------------------------------------------------
  // Private helpers — same resolver pattern as TaintSkillEffect
  // ---------------------------------------------------------------------------

  private resolveIntensity(): number {
    const base = this.config.baseIntensity;
    // Query Taint's upgrade chain (Potent Compound enhances 'taint')
    const bonus = this.deps.resolver.getAdditiveEffect('taint', 'dotDamagePerTick');
    const multiplier = this.deps.resolver.getTreePassiveEffect('poison', 'dotDamageMultiplier', 'product');
    return (base + bonus) * multiplier;
  }

  private resolveDurationMs(): number {
    const base = this.config.baseDurationMs;
    // Query Taint's upgrade chain (Lingering Formula enhances 'taint')
    const bonus = this.deps.resolver.getAdditiveEffect('taint', 'dotDurationMs');
    return base + bonus;
  }
}
